<template>
    <div class="code-input">
        <input
            v-for="(digit, index) in authCode"
            :key="index"
            ref="inputs"
            v-model="authCode[index]"
            type="tel"
            maxlength="1"
            autocomplete="one-time-code"
            inputmode="numeric"
            pattern="[0-9]*"
            :aria-label="'Digit ' + (index + 1) + ' of ' + authCode.length"
            @paste="onPaste($event, index)"
            @beforeinput="handleBeforeInput($event, index)"
            @keydown="handleKeydown($event, index)"
            @input="handleInput(index, $event)"
        />
    </div>
</template>

<script>
// The six boxes of a one-time code. Emits "auth-code-entered" with the digits
// (a list) as soon as all six are in. The code lives only in this component.
export default {
    name: 'CodeInput',
    emits: ['auth-code-entered'],
    data() {
        return {
            authCode: Array(6).fill(''),
        };
    },
    watch: {
        authCode: {
            deep: true,
            handler() {
                if (this.authCode.join('').length === this.authCode.length) {
                    this.$emit('auth-code-entered', this.authCode.slice());
                }
            },
        },
    },
    methods: {
        /** The digits typed so far, as a list. */
        digits() {
            return this.authCode.slice();
        },
        resetAuthCode() {
            this.authCode = Array(6).fill('');
        },
        focusFirst() {
            const inputs = this.$refs.inputs || [];
            if (inputs[0]) inputs[0].focus();
        },
        sanitizeAuthCode(rawCode) {
            return String(rawCode || '').replace(/\D+/g, '').slice(0, this.authCode.length);
        },
        applyAuthCode(rawCode, startIndex) {
            const index = (typeof startIndex === 'number' && startIndex >= 0) ? startIndex : 0;
            const normalizedCode = this.sanitizeAuthCode(rawCode);

            if (!normalizedCode) {
                return false;
            }

            // One assignment, so the "all six are in" check runs once.
            const next = this.authCode.slice();
            for (let i = index; i < next.length; i++) {
                next[i] = '';
            }
            normalizedCode.split('').forEach((char, offset) => {
                if (index + offset < next.length) next[index + offset] = char;
            });
            this.authCode = next;

            this.$nextTick(() => {
                const focusIndex = Math.min(index + normalizedCode.length, this.authCode.length - 1);
                const inputs = this.$refs.inputs || [];

                if (inputs[focusIndex]) {
                    inputs[focusIndex].focus();
                }
            });

            return true;
        },
        handleKeydown(e, index) {
            if (e.key === 'Backspace') {
                if (this.authCode[index]) {
                    this.authCode[index] = '';
                } else if (index > 0) {
                    this.authCode[index - 1] = '';
                    this.$refs.inputs[index - 1].focus();
                }
            }
        },
        handleBeforeInput(e, index) {
            const incomingText = (e && typeof e.data === 'string') ? e.data : '';

            if (incomingText.length > 1) {
                e.preventDefault();
                this.applyAuthCode(incomingText, index);
            }
        },
        handleInput(index, e) {
            const currentValue = String(this.authCode[index] || '');
            const incomingText = (e && typeof e.data === 'string') ? e.data : '';

            // Mobile keyboards may insert a full code without firing the paste event.
            if (incomingText.length > 1 || currentValue.length > 1) {
                this.applyAuthCode(incomingText.length > 1 ? incomingText : currentValue, index);
                return;
            }

            // Only numbers
            if (!/^[0-9]$/.test(currentValue)) {
                this.authCode[index] = '';
                return;
            }

            // On to the next box
            this.$nextTick(() => {
                if (index < this.authCode.length - 1 && this.authCode[index] !== '') {
                    this.$refs.inputs[index + 1].focus();
                }
            });
        },
        onPaste(e, index) {
            if (e && typeof e.preventDefault === 'function') {
                e.preventDefault();
            }

            let pasteText = '';

            if (e && e.clipboardData && typeof e.clipboardData.getData === 'function') {
                pasteText = e.clipboardData.getData('text');
            }

            if (!this.applyAuthCode(pasteText, index)) {
                const fallbackText = (e && e.target && typeof e.target.value === 'string') ? e.target.value : '';
                this.applyAuthCode(fallbackText, index);
            }
        },
    },
};
</script>
